import React, { useEffect, useRef, useState } from "react";
import { Box, Grid } from "@mui/material";
import CustomAlert from "../AlertComponent/Alert";
import { EventSourcePolyfill } from "event-source-polyfill";
import VARGROUP from "../VARGROUP/VARGROUP";
import { serverAddress } from "../../config";
import { Global_Data } from "../../globalData/GlobalData";
import ComponentLoadingBoundary from "../Loading/ComponentLoadingBoundary";

const MAX_EVENTS = 100;

function ListenerSection({ data, renderComponent, varValue, format }) {
  const [events, setEvents] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const { token } = Global_Data() || {};
  const eventSseErrorShownRef = useRef(false);

  const sName = data?.component?.sName;
  const sseUrl = data?.data?.sDataSource;
  const sAction = data?.data?.sAction;

  const { sx: sxFromConfig, maxHeight, ...restSProps } =
    data?.component?.sProps || {};

  const handleEventSseOpen = () => {
    eventSseErrorShownRef.current = false;
    setConnecting(false);
  };

  const handleEventSseError = err => {
    console.error("[SSE-EVENT] Connection error:", err);
    setConnecting(true);

    if (!eventSseErrorShownRef.current) {
      eventSseErrorShownRef.current = true;
      CustomAlert("error", "Cannot connect to Event SSE", {
        source: "sse",
        domain: "connection",
        action: "event"
      });
    }
  };

  const handleSseMessage = e => {
    try {
      const parsed = JSON.parse(e.data);
      setEvents(prev => [parsed, ...prev.slice(0, MAX_EVENTS - 1)]);
    } catch (_) {
      // ignore non-JSON frames
    }
  };

  const attachConfiguredEventListeners = (es, urlValue) => {
    try {
      const url = new URL(urlValue);
      const eventNames = url.searchParams.get("event")?.split(",") || [];

      eventNames.forEach(evt => {
        if (evt.trim()) {
          es.addEventListener(evt.trim(), handleSseMessage);
        }
      });
    } catch (_) {
      // invalid URL
    }
  };

  // SSE-URL: external full URL directly, no auth header
  useEffect(() => {
    if (sAction !== "SSE-URL" || !sseUrl || !sName) return;

    setConnecting(true);
    const es = new EventSource(sseUrl);

    es.onopen = handleEventSseOpen;
    es.onerror = handleEventSseError;
    es.onmessage = handleSseMessage;

    attachConfiguredEventListeners(es, sseUrl);

    return () => es.close();
  }, [sseUrl, sName, sAction]);

  // SSE: BITS Enterprise endpoint with Authorization header
  useEffect(() => {
    if (sAction !== "SSE" || !sseUrl || !sName || !token) return;

    setConnecting(true);
    const fullUrl = `${serverAddress}${sseUrl}`;

    const es = new EventSourcePolyfill(fullUrl, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      heartbeatTimeout: 120000
    });

    es.onopen = handleEventSseOpen;
    es.onerror = handleEventSseError;
    es.onmessage = handleSseMessage;

    attachConfiguredEventListeners(es, fullUrl);

    return () => es.close();
  }, [sseUrl, sName, sAction, token]);

  return (
    <Grid item {...data?.grid_props}>
      <ComponentLoadingBoundary
        loading={connecting}
        label={`Connecting ${sName}`}
        minHeight={80}
        size={24}
      >
        <Box
          sx={{
            ...sxFromConfig,
            ...restSProps,
            display: "block",
            maxHeight: maxHeight ?? "700px",
            overflowY: "auto",
            overflowX: "hidden"
          }}
        >
          <Grid container spacing={1}>
            {events.map((eventData, idx) =>
              data?.child?.map((childConfig, cIdx) => (
                <VARGROUP
                  key={`${idx}-${cIdx}`}
                  data={childConfig}
                  varValue={varValue}
                  format={format}
                  renderComponent={renderComponent}
                  overrideGroupData={eventData}
                />
              ))
            )}
          </Grid>
        </Box>
      </ComponentLoadingBoundary>
    </Grid>
  );
}

export default ListenerSection;
